import type { CanvasStep } from './types';
import { getStepStatus, STEP_META } from './types';
import styles from './WorkflowCanvas.module.css';

interface WorkflowNodeProps {
  step: CanvasStep;
  isSelected: boolean;
  availableToolNames: string[];
  availableSkillNames: string[];
  onClick: () => void;
  onDelete: () => void;
}

function StatusDot({
  step,
  toolNames,
  skillNames,
}: {
  step: CanvasStep;
  toolNames: string[];
  skillNames: string[];
}) {
  const status = getStepStatus(step, toolNames, skillNames);
  const cls =
    status === 'configured'
      ? styles.dotGreen
      : status === 'warning'
        ? styles.dotYellow
        : styles.dotRed;

  const tip =
    status === 'configured'
      ? 'Fully configured'
      : status === 'warning'
        ? 'Warning: referenced resource not found'
        : 'Required fields missing';

  return <div className={`${styles.statusDot} ${cls}`} title={tip} />;
}

/** Map step type to the icon background class */
function iconBgClass(type: CanvasStep['type']): string {
  switch (type) {
    case 'wait':       return styles.iconWait;
    case 'tool':       return styles.iconTool;
    case 'skill':      return styles.iconSkill;
    case 'condition':  return styles.iconCondition;
    case 'callback':   return styles.iconCallback;
    case 'emit_event': return styles.iconEmitEvent;
    case 'wait_event': return styles.iconWaitEvent;
    case 'loop':       return styles.iconLoop;
    default:           return '';
  }
}

export function WorkflowNode({
  step,
  isSelected,
  availableToolNames,
  availableSkillNames,
  onClick,
  onDelete,
}: WorkflowNodeProps) {
  const meta = STEP_META[step.type];
  const { Icon } = meta;

  const cardCls = [
    styles.stepCard,
    isSelected ? styles.selected : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={styles.stepNode}>
      <div
        id={`wf-node-${step.id}`}
        className={cardCls}
        onClick={onClick}
        role="button"
        tabIndex={0}
        aria-label={`${meta.label} step: ${step.name}`}
        onKeyDown={(e) => e.key === 'Enter' && onClick()}
      >
        <StatusDot step={step} toolNames={availableToolNames} skillNames={availableSkillNames} />

        {/* Delete button */}
        <button
          className={styles.deleteBtn}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          title="Remove step"
          aria-label="Remove step"
        >
          ×
        </button>

        {/* Icon — same Lucide component used in palette */}
        <div
          className={`${styles.stepIconWrap} ${iconBgClass(step.type)}`}
          style={{ color: meta.color }}
        >
          <Icon size={15} strokeWidth={2} />
        </div>

        {/* Labels */}
        <div className={styles.stepLabel}>
          <span className={styles.stepType}>{meta.label}</span>
          <span className={styles.stepName}>{step.name}</span>
          <span style={{ fontSize: '0.62rem', color: 'var(--color-text-muted)', fontFamily: 'monospace', display: 'block', marginTop: '2px', opacity: 0.8 }}>
            {step.id}
          </span>
        </div>
      </div>
    </div>
  );
}
